import { useLocation } from 'react-router-dom'
import { usePostDecisionUser } from '@/core/feature-flags'
import { UserNoticeType } from '@/generated/graphql'
import { isOpenable } from '../action-url'
import { isAppSurface, noticeSurface } from '../surfaces'
import { useCachedUserNotice, useUserNoticePeek } from './use-user-notice'

export type DeadEndCoverage =
  // Asked, not answered yet. Nobody may act on this: treating it as clear is what lets a
  // report fire a beat before the screen that was about to cover it.
  | { status: 'unknown' }
  | { status: 'clear' }
  | { status: 'covered'; actionUrl: string }

/**
 * Whether the HARD dead-end covers the route the user is on.
 *
 * Two sources, and the first that says HARD wins. The verdict the gate already asked for is
 * read from cache, so a screen the layout paid for covers instantly. Everything else — a cold
 * load into `/plan/day/:id`, a bookmark, a push deeplink — has no such verdict, so this asks
 * for its own with `peek: true`, which answers the dead-end and records no delivery. Asking
 * normally from here would spend the day's nudge on routes that never render one.
 *
 * Read by the dead-end, which renders it, AND by every exposure report, which must not fire
 * behind it: `/onboarding` and `/return` are siblings of the tabbed layout carrying reports of
 * their own, so a hold only the layout knew about left both of them counting a plan opening
 * behind a screen the user cannot leave.
 */
export function useCoveringDeadEnd(): DeadEndCoverage {
  const { pathname } = useLocation()
  // Also the session gate: without a signed-in, post-decision user there is nobody to ask
  // about, and the exposure report reads the same source, so the two flip together.
  const enabled = Boolean(usePostDecisionUser())
  const { data: asked } = useCachedUserNotice(noticeSurface(pathname))
  const { data: peeked, isPending } = useUserNoticePeek(enabled)

  if (!isAppSurface(pathname)) return { status: 'clear' }

  for (const prompt of [asked?.userNotice, peeked?.userNotice]) {
    if (prompt?.noticeType !== UserNoticeType.Hard) continue
    // The gate reports the drop where the ask happened; here it only means no dead-end.
    if (!isOpenable(prompt.actionUrl)) continue
    return { status: 'covered', actionUrl: prompt.actionUrl }
  }

  if (enabled && isPending) return { status: 'unknown' }

  return { status: 'clear' }
}
